// Typed client for the SpeedTrainer.API practice endpoints

import * as authService from './authService';
import { store } from '../store';
import { logout, sessionExpired } from '../store/authSlice';
import type {
  PracticeStatsDto,
  LeaderboardEntryDto,
  StreakLeaderboardEntryDto,
} from '@speed-trainer/shared';

export type { PracticeStatsDto, LeaderboardEntryDto, StreakLeaderboardEntryDto };

const API_BASE = (process.env.REACT_APP_API_URL ?? 'http://localhost:5244').replace(/\/$/, '');
const SUBSCRIPTION_KEY = process.env.REACT_APP_SUBSCRIPTION_KEY ?? '';

/** Builds headers with the current Bearer token if one exists. */
function authHeaders(base: Record<string, string> = {}): Record<string, string> {
  const token = authService.getStoredToken();
  const headers: Record<string, string> = { ...base };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (SUBSCRIPTION_KEY) headers['Ocp-Apim-Subscription-Key'] = SUBSCRIPTION_KEY;
  return headers;
}

/**
 * Wraps fetch with:
 *  – proactive token refresh when the token is near/past expiry
 *  – one automatic retry on 401 after a silent refresh
 *  – logout dispatch when the refresh itself fails
 */
async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  if (authService.getStoredToken() && authService.isTokenExpired()) {
    try {
      await authService.silentRefresh();
    } catch {
      store.dispatch(sessionExpired());
      return new Promise<Response>(() => {});
    }
  }

  const doFetch = (): Promise<Response> =>
    fetch(input, {
      ...init,
      headers: authHeaders(init.headers as Record<string, string>),
    });

  const res = await doFetch();

  if (res.status === 401) {
    try {
      await authService.silentRefresh();
      const retry = await doFetch();
      if (retry.status === 401) {
        store.dispatch(logout());
        return new Promise<Response>(() => {});
      }
      return retry;
    } catch {
      store.dispatch(sessionExpired());
      return new Promise<Response>(() => {});
    }
  }

  return res;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}${text ? `: ${text}` : ''}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const practiceApiService = {
  record(durationSeconds: number): Promise<void> {
    return authedFetch(`${API_BASE}/api/practice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: '', durationInSeconds: durationSeconds }),
    }).then((r) => handleResponse<void>(r));
  },

  getStats(): Promise<PracticeStatsDto> {
    return authedFetch(`${API_BASE}/api/practice/stats`).then((r) => handleResponse<PracticeStatsDto>(r));
  },

  getLeaderboard(): Promise<LeaderboardEntryDto[]> {
    return authedFetch(`${API_BASE}/api/practice/leaderboard`).then((r) => handleResponse<LeaderboardEntryDto[]>(r));
  },

  getStreakLeaderboard(): Promise<StreakLeaderboardEntryDto[]> {
    return authedFetch(`${API_BASE}/api/practice/streak-leaderboard`).then((r) => handleResponse<StreakLeaderboardEntryDto[]>(r));
  },
};
